import React, {FC} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {LinkedConversationStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {blendColors} from '../../../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {fetchAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {PopoutLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {makeDefaultCellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {createSelector} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  DetailedActivityConversationLinkedModel,
  LinkedConversationActivityRolesEnum,
} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {unlinkConversationAsyncAction} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {navigateToConversation, navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {ConversationCellContainer} from '../../../cells/conversationCellContainer';

/*
 * Props.
 */

interface ConversationSummaryCommentUnfurlViewerInheritableProps {
  panelId: ViewerPanelId;
  onRequestClose: () => void;
  isCompact?: boolean;
  numLinkedConversations?: number;
}

interface ConversationSummaryCommentUnfurlConversationViewerProps
  extends ConversationSummaryCommentUnfurlViewerInheritableProps {
  conversation: ConversationModel;
  activity?: never;
}
interface ConversationSummaryCommentUnfurlActivityViewerProps
  extends ConversationSummaryCommentUnfurlViewerInheritableProps {
  conversation?: never;
  activity: DetailedActivityConversationLinkedModel;
}

type ConversationSummaryCommentUnfurlViewerProps =
  | ConversationSummaryCommentUnfurlConversationViewerProps
  | ConversationSummaryCommentUnfurlActivityViewerProps;

/*
 * Style.
 */

const StyledCellHoverIconsWrapper = styled.div`
  display: none;

  padding: 2px;
  z-index: 2;
  border-radius: 8px;
  background: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};

  position: absolute;
  right: 5px;
  top: 5px;
  max-width: 58px;
  flex-direction: row-reverse;
`;

const StyledUnfurlButton = styled(ChromelessButton)`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  &:hover {
    background-color: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};

    ${StyledCellHoverIconsWrapper} {
      display: flex;
    }
  }
  // We disable pointer events here so the button will be the one controlling the hover/clicks and so that any actions from the cell won't work.
  & > * {
    &:not(${StyledCellHoverIconsWrapper}) {
      pointer-events: none;
    }
  }
`;

const StyledIconDiv = styled.div`
  height: 100%;
  ${center()};
`;

/*
 * Component.
 */

export const ConversationSummaryCommentUnfurlViewer: FC<ConversationSummaryCommentUnfurlViewerProps> = (
  props,
) => {
  const theme = useUserTheme();
  const {activity, conversation} = props;
  const cellsContext = useAppSelector(makeGetCellsContext());
  const dispatch = useAppDispatch();
  const {accessUrl, linkedConversation, activityId} = useGetUnfurlState(activity, conversation);
  const {trackDataEvent} = useDataEventTracking();

  if (!linkedConversation) {
    return null;
  }

  const onConversationSingleClick = async () => {
    if (accessUrl) {
      const resp = await fetchAsync(accessUrl);

      dispatch(navigateToLinkAction(resp.app_link));
    } else {
      dispatch(navigateToConversation(linkedConversation.id));
    }
    props.onRequestClose();

    if (activity) {
      trackDataEvent(
        DataEventActionsEnum.CLICK,
        LinkedConversationsDataEventObject.VIEW_LINKED_CONVERSATION,
        {
          sourceType:
            activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED
              ? LinkedConversationActivityRolesEnum.ORIGINAL
              : LinkedConversationActivityRolesEnum.LINKED,
          sourceConversationId: activity.conversationId,
          targetConversationId: activity.linkedConversationId,
        },
      );
    }
  };

  const onConversationDoubleClick = async () => {
    if (accessUrl) {
      await fetchAsync(accessUrl);
    }

    if (linkedConversation) {
      dispatch(
        popoutConversationViewerAction(
          linkedConversation.id,
          activityId,
          undefined,
          undefined,
          PopoutLocationsEnum.CONVERSATION_SUMMARY,
        ),
      );
      props.onRequestClose();
    }

    if (activity) {
      trackDataEvent(
        DataEventActionsEnum.CLICK,
        LinkedConversationsDataEventObject.VIEW_LINKED_CONVERSATION,
        {
          sourceType:
            activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED
              ? LinkedConversationActivityRolesEnum.ORIGINAL
              : LinkedConversationActivityRolesEnum.LINKED,
          sourceConversationId: activity.conversationId,
          targetConversationId: activity.linkedConversationId,
          isDoubleClick: true,
        },
      );
    }
  };

  const onUnlinkClick = async (event: FrontClickEvent) => {
    event.preventDefault();
    if (activity) {
      dispatch(unlinkConversationAsyncAction(activity));

      trackDataEvent(DataEventActionsEnum.CLICK, LinkedConversationsDataEventObject.UNLINK_CONVERSATION, {
        sourceType:
          activity.linkedConversationRole === LinkedConversationActivityRolesEnum.LINKED
            ? LinkedConversationActivityRolesEnum.ORIGINAL
            : LinkedConversationActivityRolesEnum.LINKED,
        sourceConversationId: activity.conversationId,
        targetConversationId: activity.linkedConversationId,
      });
    }
    // Only close if we're not in conversation summary and there's only 1 conversation
    if (props.isCompact && props.numLinkedConversations === 1) {
      props.onRequestClose();
    }
  };

  const unlinkLabel =
    linkedConversation.linkStatus === LinkedConversationStatusesEnum.ORIGINAL ? (
      <Trans id="conversation-summary-unlink-conversation">Unlink</Trans>
    ) : (
      <Trans id="conversation-summary-unlink-original-conversation">Unlink from original</Trans>
    );

  return (
    <StyledUnfurlButton
      onClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onConversationSingleClick();
      }}
      onDoubleClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onConversationDoubleClick();
      }}
    >
      <ConversationCellContainer
        panelId={props.panelId}
        conversation={linkedConversation}
        cellsContext={cellsContext}
        isExtraConversation={false}
        parameters={undefined}
        isTransparentPreview
        areHoverActionsEnabled
      />
      {activity && (
        <StyledCellHoverIconsWrapper>
          <StyledIconDiv>
            <IconButton
              iconName="unlinkedConversationsSmall"
              onClick={onUnlinkClick}
              label={unlinkLabel}
              labelMargin={2}
              size={VisualSizesEnum.SMALL}
              color={theme.greys.shade70}
              hoverColor={theme.greys.shade80}
            />
          </StyledIconDiv>
        </StyledCellHoverIconsWrapper>
      )}
    </StyledUnfurlButton>
  );
};

/*
 * Helpers.
 */

function makeGetCellsContext() {
  return createSelector([getUser], (user) => makeDefaultCellsContext(user, false));
}

interface UnfurlState {
  accessUrl: string | undefined;
  linkedConversation: ConversationModel | undefined;
  activityId: number | undefined;
}

function useGetUnfurlState(
  activity?: DetailedActivityConversationLinkedModel,
  conversation?: ConversationModel,
): UnfurlState {
  const conversations = useAppSelector(getConversations);
  const company = useAppSelector(getCompany);

  const maybeGetConversationFromActivity = () => {
    if (!activity?.linkedConversationId) {
      return undefined;
    }
    return conversations[activity.linkedConversationId];
  };
  const linkedConversation = conversation ?? maybeGetConversationFromActivity();

  const permalink = conversation?.conversationPermalink ?? activity?.linkedConversationPermalink;
  const accessUrl = permalink ? addQueryToPath(`${company.url}/app_link`, {link: permalink}) : undefined;

  const activityId = activity?.id;

  return {accessUrl, linkedConversation, activityId};
}
